import { SchoolLevelType } from "@/providers/store/api/schools/schools.interface";

import type maplibregl from "maplibre-gl";

export const SCHOOL_LEVEL_TYPES_FOR_CSV: SchoolLevelType[] = [
  SchoolLevelType.ELEMENTARY,
  SchoolLevelType.HIGH,
  SchoolLevelType.K8,
  SchoolLevelType.MIDDLE,
];

/** MVT `types`: comma-separated scalar string from PostGIS `array_to_string(..., ',')`. */
export const schoolTypesRawStringExpression = [
  "coalesce",
  ["to-string", ["get", "types"]],
  "",
] as unknown as maplibregl.ExpressionSpecification;

/**
 * `"," + types + ","` so whole-token checks use `index-of` without `split` (wider runtime support).
 */
export const schoolTypesPaddedCsvExpression = [
  "concat",
  ",",
  schoolTypesRawStringExpression,
  ",",
] as unknown as maplibregl.ExpressionSpecification;

/** True when comma-separated `types` contains `typeLiteral` as a full token. */
export function schoolTypeContainedInTypesCsvExpression(
  typeLiteral: string,
): maplibregl.ExpressionSpecification {
  return [
    ">=",
    ["index-of", ["concat", ",", typeLiteral, ","], schoolTypesPaddedCsvExpression],
    0,
  ] as unknown as maplibregl.ExpressionSpecification;
}

/** First CSV token (same order as SQL `array_agg(... ORDER BY type)`). */
export const schoolTypesFirstTokenExpression = [
  "case",
  ["==", schoolTypesRawStringExpression, ""],
  "",
  ["==", ["index-of", ",", schoolTypesRawStringExpression], -1],
  schoolTypesRawStringExpression,
  [
    "slice",
    schoolTypesRawStringExpression,
    0,
    ["index-of", ",", schoolTypesRawStringExpression],
  ],
] as unknown as maplibregl.ExpressionSpecification;

export const schoolTypesCsvIsEmptyExpression = [
  "==",
  schoolTypesRawStringExpression,
  "",
] as unknown as maplibregl.ExpressionSpecification;

export const schoolTypesCsvNonEmptyExpression = [
  "!=",
  schoolTypesRawStringExpression,
  "",
] as unknown as maplibregl.ExpressionSpecification;

export function coerceSchoolTypesArray(value: unknown): string[] {
  if (typeof value === "string") {
    return value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((x): x is string => typeof x === "string");
}

export function schoolTypesArrayIntersects(types: unknown, needles: string[]): boolean {
  if (needles.length === 0) {
    return true;
  }
  const tokens = coerceSchoolTypesArray(types);
  if (tokens.length === 0) {
    return false;
  }
  const set = new Set(needles);
  return tokens.some((t) => set.has(t));
}

export function schoolTypesArrayFullyHidden(types: unknown, hiddenTypes: string[]): boolean {
  const tokens = coerceSchoolTypesArray(types);
  if (tokens.length === 0) {
    return false;
  }
  return tokens.every((t) => hiddenTypes.includes(t));
}

export function buildSchoolLevelTypesArrayVisibleExpression(
  hiddenTypes: string[],
): maplibregl.ExpressionSpecification {
  const branches: maplibregl.ExpressionSpecification[] = SCHOOL_LEVEL_TYPES_FOR_CSV.map(
    (T) =>
      [
        "all",
        schoolTypeContainedInTypesCsvExpression(T),
        ["!", ["in", T, ["literal", hiddenTypes]]],
      ] as maplibregl.ExpressionSpecification,
  );
  return [
    "any",
    ...branches,
    schoolTypesCsvIsEmptyExpression,
  ] as maplibregl.ExpressionSpecification;
}

export function buildSchoolTypesArrayIntersectsAnyExpression(
  mergedTypes: string[],
): maplibregl.ExpressionSpecification {
  if (mergedTypes.length === 0) {
    return ["literal", false] as maplibregl.ExpressionSpecification;
  }
  return [
    "any",
    ...mergedTypes.map((T) => schoolTypeContainedInTypesCsvExpression(T)),
  ] as maplibregl.ExpressionSpecification;
}
